import React, { useState, useMemo } from 'react';
import { Alert, AlertDescription } from '../ui/Alert';
import { Badge } from '../ui/Badge';
import { Droplets, Building2, Leaf, AlertCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { calculateEcoScores } from './ecoScoreCalculator';
import './EcoScoreBoard.css';

const ScoreCard = ({ icon: Icon, title, score, color, details, isOpen, toggleOpen }) => (
  <div className="score-card bg-white p-2 rounded-lg shadow-sm transition-transform transform hover:scale-105">
    <div className="flex items-center justify-between mb-2 cursor-pointer" onClick={toggleOpen}>
      <div className="flex items-center gap-2">
        <Icon className={`w-5 h-5 ${color}`} />
        <h3 className="text-sm font-semibold text-gray-700">{title}</h3>
      </div>
      {isOpen ? <ChevronUp className="w-4 h-4 text-gray-600" /> : <ChevronDown className="w-4 h-4 text-gray-600" />}
    </div>
    {isOpen && (
      <div className="flex justify-between items-center">
        <span className={`text-lg font-bold ${color}`}>{Math.round(score)}%</span>
        {details && <Badge variant="secondary" className="text-xs px-2 py-0.5">{details}</Badge>}
      </div>
    )}
  </div>
);

const Suggestions = ({ scores }) => {
  const suggestions = [];
  
  if (scores.greenSpace < 30) suggestions.push('Add more green spaces');
  if (scores.waterManagement < 30) suggestions.push('Add water features');
  if (scores.sustainableDesign < 30) suggestions.push('Add sustainable buildings');
  if (scores.biodiversity < 30) suggestions.push('Increase biodiversity');

  if (!suggestions.length) return null;

  return (
    <Alert className="suggestions-alert p-3 mt-4 bg-yellow-100 rounded-md">
      <AlertCircle className="h-4 w-4 text-yellow-600" />
      <AlertDescription>
        <div className="text-sm font-medium mb-1 text-yellow-800">Suggestions to improve:</div>
        <ul className="suggestions-list pl-5 text-sm list-disc text-yellow-700">
          {suggestions.map((suggestion, index) => (
            <li key={index}>{suggestion}</li>
          ))}
        </ul>
      </AlertDescription>
    </Alert>
  );
};

const EcoScoreBoard = ({ elements }) => {
  const scores = useMemo(() => calculateEcoScores(elements), [elements]);

  const [isBoardOpen, setBoardOpen] = useState(false); // State to control the entire board
  const [openSections, setOpenSections] = useState({
    greenSpace: false,
    waterManagement: false,
    sustainableDesign: false,
    biodiversity: false
  });

  const toggleBoard = () => setBoardOpen(!isBoardOpen); // Toggle for the entire board
  const toggleSection = (section) => setOpenSections((prev) => ({ ...prev, [section]: !prev[section] }));

  const getScoreClass = (score) => {
    if (score >= 80) return 'text-green-700';
    if (score >= 60) return 'text-yellow-500';
    return 'text-red-500';
  };

  const getScoreDetails = (type, score) => {
    switch(type) {
      case 'greenSpace':
        return `${Math.round((score / 100) * elements.length)} green spaces`;
      case 'waterManagement':
        return `${elements.filter(e => e.properties.hasWaterFeature).length} water features`;
      case 'sustainableDesign':
        return `${elements.filter(e => e.properties.hasGreenRoof || e.properties.hasSolarPanels).length} sustainable designs`;
      case 'biodiversity':
        return `${elements.filter(e => e.properties.supportsBiodiversity).length} biodiversity areas`;
      default:
        return null;
    }
  };

  return (
    <div className="eco-score-board-container bg-gray-50 p-6 rounded-xl shadow-lg">
      <div className="flex justify-between items-center mb-4 cursor-pointer" onClick={toggleBoard}>
        <div>
          <h2 className="text-lg font-bold text-gray-800">Eco-Friendly Score</h2>
          <p className="text-xs text-gray-500">Overall township eco-rating</p>
        </div>
        {isBoardOpen ? <ChevronUp className="w-6 h-6 text-gray-600" /> : <ChevronDown className="w-6 h-6 text-gray-600" />}
      </div>
      
      {isBoardOpen && (
        <>
          <div className="mb-4 flex justify-between items-center">
            <div className={`text-2xl font-extrabold ${getScoreClass(scores.overall)}`}>
              {scores.overall}%
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <ScoreCard 
              icon={Droplets}
              title="Green Space"
              score={scores.greenSpace}
              color="text-green-700"
              details={getScoreDetails('greenSpace', scores.greenSpace)}
              isOpen={openSections.greenSpace}
              toggleOpen={() => toggleSection('greenSpace')}
            />
            <ScoreCard 
              icon={Droplets}
              title="Water Management"
              score={scores.waterManagement}
              color="text-blue-700"
              details={getScoreDetails('waterManagement', scores.waterManagement)}
              isOpen={openSections.waterManagement}
              toggleOpen={() => toggleSection('waterManagement')}
            />
            <ScoreCard 
              icon={Building2}
              title="Sustainable Design"
              score={scores.sustainableDesign}
              color="text-purple-700"
              details={getScoreDetails('sustainableDesign', scores.sustainableDesign)}
              isOpen={openSections.sustainableDesign}
              toggleOpen={() => toggleSection('sustainableDesign')}
            />
            <ScoreCard 
              icon={Leaf}
              title="Biodiversity"
              score={scores.biodiversity}
              color="text-green-700"
              details={getScoreDetails('biodiversity', scores.biodiversity)}
              isOpen={openSections.biodiversity}
              toggleOpen={() => toggleSection('biodiversity')}
            />
          </div>

          <Suggestions scores={scores} />
        </>
      )}
    </div>
  );
};

export default EcoScoreBoard;
